import { createContext, Dispatch, PropsWithChildren, useCallback, useContext, useReducer } from 'react';
import { Payload } from './Payload';
import { DashboardDealer, DashboardPermission, DashboardUserPermission, DashboardUserProfile, DealerGroup, LoggedInUser, UpdateProfileResponse } from '../API/APIObjects';
import store from 'store2';
import { useAPICall, useAxiosAbortController, useResetSession } from './Axios';
import { MessageDispatchTypes, useMessageDispatch } from './Message';
import { LandingPage, PermissionLevel, UserPermissions } from '../API/Enums';
import clone from 'just-clone';
import { SessionStorageKeys, SessionStorageKeyType } from '../CommonUtils/Storage';
import { GMSelectOption } from '../CommonControls/Inputs/Select';
import { useLocalization } from './Localization';
import { useNavigate } from 'react-router-dom';
import { AppRoutes } from '../CommonUtils/Routes';

export enum UserDispatchTypes {
    Login = 'LOGIN',
    Logout = 'LOGOUT',
    Update = 'UPDATE'
}

type UserPayload = {
    [UserDispatchTypes.Login]: LoggedInUser;
    [UserDispatchTypes.Update]: LoggedInUser;
    [UserDispatchTypes.Logout]: never;
};

type UserActions = Payload<UserPayload>[keyof Payload<UserPayload>];

type UserStateType = {
    UserData: LoggedInUser;
    LoggedIn: boolean;
};

const initialState: UserStateType = {
    UserData: {
        token: '',
        refresh_token: '',
        id: 0,
        username: '',
        password: '',
        firstname: '',
        lastname: '',
        email: '',
        is_staff: false,
        accessible_dealers: [],
        accessible_groups: [],
        permissions: [],
        temp_password: false,
        phone_number: '',
        notifications: [],
        advisor_numbers: [],
        is_active: false,
        is_locked: false,
        date_locked: null,
        reason_locked: null,
        dsim_userid: '',
        pin: '',
        default_page: LandingPage.Summary,
        default_support_page: LandingPage.Kiosk,
        default_dealer: null,
        profile_picture: '',
        role_id: 0,
        is_auth_user: false,
        is_super_user: false,
        show_key_management_menu: false,
        show_qr_code_scan_menu: false,
        show_whats_new: false,
        login_session: '',
        mfa_enabled: false,
    },
    LoggedIn: false,
};

const userReducer = (state: UserStateType, action: UserActions): UserStateType => {
    let newState: UserStateType;

    switch (action.type) {
        case UserDispatchTypes.Login:
            store.session.set(SessionStorageKeys.LoginSession, action.payload.login_session);

            newState = {
                ...state,
                UserData: {
                    ...action.payload,
                },
                LoggedIn: true,
            };
            break;
        case UserDispatchTypes.Logout:
            newState = {
                ...state,
                UserData: {
                    ...initialState.UserData,
                },
                LoggedIn: false,
            };
            break;
        case UserDispatchTypes.Update:
            store.session.set(SessionStorageKeys.Username, action.payload.username);

            newState = {
                ...state,
                UserData: {
                    ...action.payload,
                },
            };
            break;
        default:
            newState = state;
            break;
    }

    if (newState.UserData.username) {
        store.session.set(SessionStorageKeys.Username, newState.UserData.username);
    }

    return newState;
};

const UserStateContext = createContext<UserStateType>(initialState);
const UserDispatchContext = createContext<Dispatch<UserActions>>(() => null);

export const UserProvider: React.FC<PropsWithChildren> = (props) => {
    const [state, dispatch] = useReducer(userReducer, initialState);

    return (
        <UserStateContext.Provider value={state}>
            <UserDispatchContext.Provider value={dispatch}>
                {props.children}
            </UserDispatchContext.Provider>
        </UserStateContext.Provider>
    );
};

export const useUserState = () => {
    const context = useContext(UserStateContext);
    if (!context) {
        throw new Error('No User Context');
    }

    return context;
};

export const useUserDispatch = () => {
    const context = useContext(UserDispatchContext);
    if (context === undefined) {
        throw new Error('No User Dispatch Context');
    }

    return context;
};

export const useLogin = () => {
    const userDispatch = useUserDispatch();
    const resetSession = useResetSession();

    return useCallback((user: LoggedInUser) => {
        resetSession();
        userDispatch({
            type: UserDispatchTypes.Login,
            payload: user,
        });
    }, [userDispatch, resetSession]);
};

export const useLogout = () => {
    const userDispatch = useUserDispatch();
    const messageDispatch = useMessageDispatch();
    const api = useAPICall();
    const axiosAbortController = useAxiosAbortController();
    const navigate = useNavigate();

    return useCallback((auto: boolean) => {
        api<void>(
            undefined,
            undefined,
            undefined,
            () => {
                store.session.clear();
                axiosAbortController();
                messageDispatch({ type: MessageDispatchTypes.Clear });
                userDispatch({ type: UserDispatchTypes.Logout });
                navigate(AppRoutes.Home);
            },
        )
            .put(`Login/Logout?auto=${auto}`, JSON.stringify(store.session.get(SessionStorageKeys.LoginSession)));
    }, [userDispatch, api, axiosAbortController, messageDispatch, navigate]);
};

export const useUpdateUser = () => {
    const userDispatch = useUserDispatch();

    return useCallback((user: LoggedInUser) => {
        userDispatch({
            type: UserDispatchTypes.Update,
            payload: user,
        });
    }, [userDispatch]);
};

export const useGetPermissionLevel = () => {
    const { UserData } = useUserState();

    return useCallback((permission: UserPermissions) => {
        let foundPermission: DashboardUserPermission | undefined = UserData.permissions?.find((p) => p.permission === permission);

        if (foundPermission) {
            return foundPermission.permissionLevel;
        }

        return PermissionLevel.None;
    }, [UserData.permissions]);
};

export const useHasFullAccess = () => {
    const getPermissionLevel = useGetPermissionLevel();

    return useCallback((permission: UserPermissions) => {
        return getPermissionLevel(permission) === PermissionLevel.YesFull;
    }, [getPermissionLevel]);
};

export const useHasViewAccess = () => {
    const getPermissionLevel = useGetPermissionLevel();

    return useCallback((permission: UserPermissions) => {
        let permissionLevel: PermissionLevel = getPermissionLevel(permission);

        return permissionLevel === PermissionLevel.YesFull || permissionLevel === PermissionLevel.View;
    }, [getPermissionLevel]);
};

export const useHasLesserPermissions = () => {
    const getPermissionLevel = useGetPermissionLevel();

    return useCallback((permissions: DashboardUserPermission[] | DashboardPermission[]) => {
        for (let i: number = 0; i < permissions.length; i++) {
            const permission: DashboardUserPermission | DashboardPermission = permissions[i];
            const loggedInUserPermission: PermissionLevel = getPermissionLevel(permission.permission);

            if (loggedInUserPermission !== permission.permissionLevel) {
                switch (permission.permissionLevel) {
                    case PermissionLevel.YesFull:
                        return true;
                    case PermissionLevel.View:
                        if (loggedInUserPermission === PermissionLevel.None) {
                            return true;
                        }
                        break;
                    case PermissionLevel.None:
                    default:
                        break;
                }
            }
        }

        return false;
    }, [getPermissionLevel]);
};

export const useIsPermissionLevelAllowed = () => {
    const getPermissionLevel = useGetPermissionLevel();

    return useCallback((permission: UserPermissions, level: PermissionLevel) => {
        const loggedInUserPermissionLevel: PermissionLevel = getPermissionLevel(permission);

        switch (loggedInUserPermissionLevel) {
            case PermissionLevel.View:
                return level !== PermissionLevel.YesFull;
            case PermissionLevel.YesFull:
                return true;
            case PermissionLevel.None:
            default:
                return level === PermissionLevel.None;
        }
    }, [getPermissionLevel]);
};

/**
 * Saves changes to the user profile and updates the logged in user information
 */
export const useUpdateUserProfile = () => {
    const { UserData } = useUserState();
    const api = useAPICall();
    const updateUser = useUpdateUser();

    return useCallback(
        (newUserProfile: DashboardUserProfile,
            onSuccess?: (updatedLoggedInUser: LoggedInUser) => void,
            onError?: (error: string) => void,
            onLoading?: (loading: boolean) => void,
            onComplete?: () => void,
        ) => {
            api<UpdateProfileResponse>(
                (data) => {
                    let newUserData: LoggedInUser = {
                        ...clone(UserData),
                        username: data.username,
                        email: newUserProfile.email,
                        firstname: newUserProfile.firstName,
                        lastname: newUserProfile.lastName,
                        phone_number: newUserProfile.phoneNumber,
                        notifications: newUserProfile.notifications,
                        default_page: newUserProfile.defaultPage,
                        default_support_page: newUserProfile.defaultSupportPage,
                        default_dealer: newUserProfile.defaultDealer,
                        profile_picture: data.profile_picture,
                        password: '',
                        pin: UserData.is_staff ? newUserProfile.pin : '',
                    };

                    updateUser(newUserData);
                    onSuccess && onSuccess(newUserData);
                },
                onLoading,
                onError,
                onComplete,
            )
                .post(
                    'Reporting/UpdateUserProfile',
                    newUserProfile,
                );
        }, [UserData, api, updateUser]);
};

export const useGetUserDealerOptions = () => {
    const { UserData } = useUserState();

    return useCallback((onlyKiosksWithStorage: boolean = false): GMSelectOption[] => {
        return UserData.accessible_dealers.reduce((options: GMSelectOption[], dealer: DashboardDealer) => {
            if (!onlyKiosksWithStorage || dealer.hasKiosksWithStorage) {
                options.push({
                    value: dealer.key,
                    label: dealer.name,
                });
            }

            return options;
        }, []);
    }, [UserData]);
};

const getDefaultDealer = (defaultDealerId: number | null, accessibleDealers: DashboardDealer[]) => {
    let initialDealer: string = '';
    let defaultDealerIndex: number = -1;

    if (defaultDealerId !== null) {
        defaultDealerIndex = accessibleDealers.findIndex((d) => d.id === defaultDealerId);
    }

    if (defaultDealerIndex > -1) {
        initialDealer = accessibleDealers[defaultDealerIndex].key;
    }
    else if (accessibleDealers.length > 0) {
        initialDealer = accessibleDealers[0].key;
    }

    return initialDealer;
};

/**
 * Get dealer value for a dealer single select
 */
export const getInitialDealer = (
    defaultDealerId: number | null,
    accessibleDealers: DashboardDealer[],
    sessionKey: SessionStorageKeyType = SessionStorageKeys.DealersSelected,
): string => {
    const storedDealers: string[] | undefined = store.session.get(sessionKey);

    if (storedDealers && storedDealers.length > 0) {
        return storedDealers[0];
    }
    else {
        const initialDealer: string = getDefaultDealer(defaultDealerId, accessibleDealers);

        store.session.set(sessionKey, [initialDealer]);

        return initialDealer;
    }
};

/**
 * Get dealer values for a dealer multiselect
 */
export const getInitialDealers = (
    defaultDealerId: number | null,
    accessibleDealers: DashboardDealer[],
    sessionKey: SessionStorageKeyType = SessionStorageKeys.DealersSelected,
): string[] => {
    if (store.session.has(sessionKey)) {
        return store.session.get(sessionKey);
    }
    else {
        const initialDealers: string[] = [getDefaultDealer(defaultDealerId, accessibleDealers)];

        store.session.set(sessionKey, initialDealers);

        return initialDealers;
    }
};

export const useGetUserDmsOptions = () => {
    const { UserData } = useUserState();

    return useCallback((): GMSelectOption[] => {
        const dmsOptions: GMSelectOption[] = UserData.accessible_dealers.reduce((options: GMSelectOption[], dealer: DashboardDealer) => {
            if (dealer.dmsName !== '0' && !options.find((o) => o.value === dealer.dmsCode)) {
                options.push({
                    value: dealer.dmsCode,
                    label: dealer.dmsName,
                });
            }

            return options;
        }, []);

        return dmsOptions.sort((a, b) => {
            const optA: string = a.label.toLowerCase();
            const optB: string = b.label.toLowerCase();
            if (optA < optB) {
                return -1;
            }
            if (optA > optB) {
                return 1;
            }
            return 0;
        });
    }, [UserData]);
};

export const useGetUserDealerGroupOptions = () => {
    const { UserData } = useUserState();

    return useCallback((): GMSelectOption[] => {
        return UserData.accessible_groups.map((group: DealerGroup) => {
            return {
                value: group.key,
                label: group.name,
            };
        });
    }, [UserData]);
};

export const useGetUserBrandOptions = () => {
    const { UserData } = useUserState();

    return useCallback((): GMSelectOption[] => {
        const brands: string[] = UserData.accessible_dealers.reduce((options: string[], dealer: DashboardDealer) => {
            if (dealer.make) {
                dealer.make.replace(/, |\//g, '#')
                    .split('#')
                    .forEach((make) => {
                        if (!options.includes(make)) {
                            options.push(make);
                        }
                    });
            }

            return options;
        }, []);

        return brands.sort()
            .map((brand) => {
                return {
                    label: brand,
                    value: brand,
                };
            });
    }, [UserData]);
};

export const useGetUserRegionOptions = () => {
    const Localization = useLocalization();

    return useCallback((): GMSelectOption[] => {
        return [
            {
                label: Localization.CanadaCentral,
                value: 'canadacentral',
            },
            {
                label: Localization.CanadaEast,
                value: 'canadaeast',
            },
            {
                label: Localization.CanadaOntario,
                value: 'canadaontario',
            },
            {
                label: Localization.CanadaQuebec,
                value: 'canadaquebec',
            },
            {
                label: Localization.CanadaWest,
                value: 'canadawest',
            },
            {
                label: Localization.USAMidwest,
                value: 'usamidwest',
            },
            {
                label: Localization.USANortheast,
                value: 'usanortheast',
            },
            {
                label: Localization.USASoutheast,
                value: 'usasoutheast',
            },
            {
                label: Localization.USAWest,
                value: 'usawest',
            },
        ];
    }, [Localization]);
};
